import * as React from "react";

import styles from "./TabStrip.module.css";
import { useTabStripScroll } from "./useTabStripScroll";

export interface TabStripItem {
  key: string;
  label: React.ReactNode;
  count?: number;
  countBg?: string;
  countColor?: string;
  disabled?: boolean;
}

export interface TabStripProps {
  tabs: TabStripItem[];
  activeKey: string;
  onChange: (key: string) => void;
  /** horizontal scrollable strip with fade + drag (right panel); otherwise plain underline tabs */
  scrollable?: boolean;
  className?: string;
}

export const TabStrip: React.FC<TabStripProps> = ({ tabs, activeKey, onChange, scrollable, className }) => {
  const scroll = useTabStripScroll(!!scrollable);

  const items = tabs.map((t) => {
    const active = t.key === activeKey;
    return (
      <span
        key={t.key}
        role="tab"
        aria-selected={active}
        aria-disabled={t.disabled || undefined}
        onClick={() => !t.disabled && onChange(t.key)}
        className={`${styles.tab}${active ? ` ${styles.active}` : ``}${t.disabled ? ` ${styles.disabled}` : ``}`}
      >
        {t.label}
        {t.count != null && (
          <span
            className={styles.count}
            style={{ background: t.countBg, color: t.countColor }}
          >
            {t.count}
          </span>
        )}
      </span>
    );
  });

  if (!scrollable) {
    return <div className={`${styles.strip} ${styles.underline}${className ? ` ${className}` : ``}`}>{items}</div>;
  }

  return (
    <div className={`${styles.scrollWrap}${className ? ` ${className}` : ``}`}>
      <div
        ref={scroll.stripRef}
        className={`${styles.strip} ${styles.scrollable}`}
        onClickCapture={scroll.onClickCapture}
      >
        {items}
      </div>
      <div
        className={`${styles.fade} ${styles.fadeLeft}`}
        style={{ opacity: scroll.showLeft ? 1 : 0, pointerEvents: scroll.showLeft ? "auto" : "none" }}
        onClick={() => scroll.scrollBy(-160)}
      >
        <i className="fa-solid fa-chevron-left" />
      </div>
      <div
        className={`${styles.fade} ${styles.fadeRight}`}
        style={{ opacity: scroll.showRight ? 1 : 0, pointerEvents: scroll.showRight ? "auto" : "none" }}
        onClick={() => scroll.scrollBy(160)}
      >
        <i className="fa-solid fa-chevron-right" />
      </div>
    </div>
  );
};
